import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, CreditCard, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';

const PlansManagement = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  
  const [form, setForm] = useState({
    name: '',
    scope: 'global',
    target_id: '',
    category_ids: [],
    price: 0,
    currency: 'EUR',
    interval: 'month',
    stripe_price_id: '',
    description_el: '',
    description_en: '',
    sales_eyebrow_el: '',
    sales_eyebrow_en: '',
    sales_title_el: '',
    sales_title_en: '',
    sales_subtitle_el: '',
    sales_subtitle_en: '',
    sales_description_el: '',
    sales_description_en: '',
    sales_included_el: [],
    sales_included_en: [],
    image_url: '',
    image_alt_el: '',
    image_alt_en: '',
    is_active: true
  });

  const arrayToLines = (value) => Array.isArray(value) ? value.join('\n') : '';
  const linesToArray = (value) => (value || '').split('\n').map((line) => line.trim()).filter(Boolean);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [plansRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/plans`, { headers }),
        axios.get(`${API}/admin/categories`, { headers })
      ]);
      setPlans(plansRes.data || []);
      setCategories(catsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch plans', e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      scope: 'global',
      target_id: '',
      category_ids: [],
      price: 0,
      currency: 'EUR',
      interval: 'month',
      stripe_price_id: '',
      description_el: '',
      description_en: '',
      sales_eyebrow_el: '',
      sales_eyebrow_en: '',
      sales_title_el: '',
      sales_title_en: '',
      sales_subtitle_el: '',
      sales_subtitle_en: '',
      sales_description_el: '',
      sales_description_en: '',
      sales_included_el: [],
      sales_included_en: [],
      image_url: '',
      image_alt_el: '',
      image_alt_en: '',
      is_active: true
    });
    setEditingPlan(null);
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name || '',
      scope: plan.scope || 'global',
      target_id: plan.target_id || '',
      category_ids: plan.category_ids || (plan.target_id ? [plan.target_id] : []),
      price: plan.price || 0,
      currency: plan.currency || 'EUR',
      interval: plan.interval || 'month',
      stripe_price_id: plan.stripe_price_id || '',
      description_el: plan.description_el || '',
      description_en: plan.description_en || '',
      sales_eyebrow_el: plan.sales_eyebrow_el || '',
      sales_eyebrow_en: plan.sales_eyebrow_en || '',
      sales_title_el: plan.sales_title_el || '',
      sales_title_en: plan.sales_title_en || '',
      sales_subtitle_el: plan.sales_subtitle_el || '',
      sales_subtitle_en: plan.sales_subtitle_en || '',
      sales_description_el: plan.sales_description_el || '',
      sales_description_en: plan.sales_description_en || '',
      sales_included_el: plan.sales_included_el || [],
      sales_included_en: plan.sales_included_en || [],
      image_url: plan.image_url || '',
      image_alt_el: plan.image_alt_el || '',
      image_alt_en: plan.image_alt_en || '',
      is_active: plan.is_active !== false
    });
    setDialogOpen(true);
  };

  const uploadPlanImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/uploads/image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm((prev) => ({ ...prev, image_url: res.data?.url || prev.image_url }));
      toast.success(i18n.language === 'el' ? 'Η εικόνα ανέβηκε' : 'Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image upload failed');
    } finally {
      setImageUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingPlan) {
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, form, { headers });
        toast.success(i18n.language === 'el' ? 'Το πλάνο ενημερώθηκε' : 'Plan updated');
      } else {
        await axios.post(`${API}/admin/plans`, form, { headers });
        toast.success(i18n.language === 'el' ? 'Το πλάνο δημιουργήθηκε' : 'Plan created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleSyncStripe = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/plans/sync-stripe`, {}, { headers });
      const data = res.data;
      if (data.synced > 0) {
        toast.success(`${data.synced}/${data.total} ${i18n.language === 'el' ? 'πλάνα συγχρονίστηκαν με το Stripe' : 'plans synced to Stripe'}`);
      } else if (data.success === false) {
        toast.error(data.message || 'Error');
      } else {
        toast.info(i18n.language === 'el' ? 'Όλα τα πλάνα είναι ήδη συνδεδεμένα' : 'All plans already synced');
      }
      if (data.errors?.length > 0) {
        data.errors.forEach(err => toast.error(`${err.plan}: ${err.error}`));
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(i18n.language === 'el' ? 'Είστε σίγουροι;' : 'Are you sure?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/plans/${id}`, { headers });
      toast.success(i18n.language === 'el' ? 'Το πλάνο διαγράφηκε' : 'Plan deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const getScopeLabel = (scope) => {
    const labels = {
      global: i18n.language === 'el' ? 'Όλα τα Βίντεο' : 'All Videos',
      category: i18n.language === 'el' ? 'Κατηγορία' : 'Category'
    };
    return labels[scope] || scope;
  };

  const getIntervalLabel = (interval) => {
    const labels = {
      month: i18n.language === 'el' ? 'Μηνιαία' : 'Monthly',
      year: i18n.language === 'el' ? 'Ετήσια' : 'Yearly'
    };
    return labels[interval] || interval;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 lg:mb-8">
        <h1 className="font-roboto font-bold text-lg lg:text-2xl text-white flex items-center gap-2">
          <CreditCard className="text-primary" />
          {i18n.language === 'el' ? 'Πλάνα Συνδρομών' : 'Subscription Plans'}
        </h1>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            onClick={handleSyncStripe}
            data-testid="sync-stripe-btn"
          >
            {i18n.language === 'el' ? 'Συγχρονισμός Stripe' : 'Sync to Stripe'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus size={18} className="mr-2" />
              {i18n.language === 'el' ? 'Νέο Πλάνο' : 'New Plan'}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-misel-card border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-roboto font-bold">
                {editingPlan 
                  ? (i18n.language === 'el' ? 'Επεξεργασία Πλάνου' : 'Edit Plan')
                  : (i18n.language === 'el' ? 'Νέο Πλάνο' : 'New Plan')
                }
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Όνομα Πλάνου' : 'Plan Name'}
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder={i18n.language === 'el' ? 'π.χ. Premium Monthly' : 'e.g. Premium Monthly'}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">
                    {i18n.language === 'el' ? 'Τύπος' : 'Scope'}
                  </label>
                  <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, target_id: '', category_ids: [] })}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">{i18n.language === 'el' ? 'Όλα τα Βίντεο' : 'All Videos'}</SelectItem>
                      <SelectItem value="category">{i18n.language === 'el' ? 'Κατηγορία' : 'Category'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.scope === 'category' && (
                  <div>
                    <label className="text-sm text-gray-400">
                      {i18n.language === 'el' ? 'Κατηγορίες' : 'Categories'}
                    </label>
                    <div className="mt-1 space-y-2 max-h-48 overflow-y-auto bg-white/5 border border-white/10 rounded-md p-3">
                      {categories.map((cat) => {
                        const catName = cat.translations?.el?.title || cat.title || cat.slug;
                        const isChecked = form.category_ids.includes(cat.id);
                        return (
                          <label key={cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const newIds = isChecked
                                  ? form.category_ids.filter(id => id !== cat.id)
                                  : [...form.category_ids, cat.id];
                                setForm({ ...form, category_ids: newIds, target_id: newIds[0] || '' });
                              }}
                              className="rounded border-white/20 bg-white/5 text-primary"
                              data-testid={`plan-cat-${cat.id}`}
                            />
                            <span className="text-white text-sm">{catName}</span>
                          </label>
                        );
                      })}
                      {categories.length === 0 && (
                        <p className="text-gray-500 text-sm">{i18n.language === 'el' ? 'Δεν υπάρχουν κατηγορίες' : 'No categories'}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {form.category_ids.length} {i18n.language === 'el' ? 'επιλεγμένες' : 'selected'}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400">
                    {i18n.language === 'el' ? 'Τιμή' : 'Price'}
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    {i18n.language === 'el' ? 'Νόμισμα' : 'Currency'}
                  </label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    {i18n.language === 'el' ? 'Περίοδος' : 'Interval'}
                  </label>
                  <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">{i18n.language === 'el' ? 'Μηνιαία' : 'Monthly'}</SelectItem>
                      <SelectItem value="year">{i18n.language === 'el' ? 'Ετήσια' : 'Yearly'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400">
                  Stripe Price ID
                  <span className="text-xs text-gray-600 ml-1">
                    ({i18n.language === 'el' ? 'π.χ. price_xxxxx - για αυτόματη μηνιαία χρέωση' : 'e.g. price_xxxxx - for automatic recurring billing'})
                  </span>
                </label>
                <Input
                  value={form.stripe_price_id}
                  onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder="price_1Nxxxxxxxxxxxxx"
                  data-testid="stripe-price-id-input"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Περιγραφή (EL)' : 'Description (EL)'}
                </label>
                <Input
                  value={form.description_el}
                  onChange={(e) => setForm({ ...form, description_el: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Περιγραφή (EN)' : 'Description (EN)'}
                </label>
                <Input
                  value={form.description_en}
                  onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>

              <div className="border border-white/10 rounded-sm p-4 space-y-4">
                <h3 className="text-white font-medium">Commercial pricing card content</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Eyebrow EL</label>
                    <Input value={form.sales_eyebrow_el} onChange={(e) => setForm({ ...form, sales_eyebrow_el: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Eyebrow EN</label>
                    <Input value={form.sales_eyebrow_en} onChange={(e) => setForm({ ...form, sales_eyebrow_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Sales title EL</label>
                    <Input value={form.sales_title_el} onChange={(e) => setForm({ ...form, sales_title_el: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Sales title EN</label>
                    <Input value={form.sales_title_en} onChange={(e) => setForm({ ...form, sales_title_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Subtitle EL</label>
                    <Textarea value={form.sales_subtitle_el} onChange={(e) => setForm({ ...form, sales_subtitle_el: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 min-h-20" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Subtitle EN</label>
                    <Textarea value={form.sales_subtitle_en} onChange={(e) => setForm({ ...form, sales_subtitle_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 min-h-20" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Description EL</label>
                    <Textarea value={form.sales_description_el} onChange={(e) => setForm({ ...form, sales_description_el: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 min-h-28" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Description EN</label>
                    <Textarea value={form.sales_description_en} onChange={(e) => setForm({ ...form, sales_description_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 min-h-28" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Included bullets EL, one per line</label>
                    <Textarea value={arrayToLines(form.sales_included_el)} onChange={(e) => setForm({ ...form, sales_included_el: linesToArray(e.target.value) })} className="bg-white/5 border-white/10 text-white mt-1 min-h-32" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Included bullets EN, one per line</label>
                    <Textarea value={arrayToLines(form.sales_included_en)} onChange={(e) => setForm({ ...form, sales_included_en: linesToArray(e.target.value) })} className="bg-white/5 border-white/10 text-white mt-1 min-h-32" />
                  </div>
                </div>
              </div>

              <div className="border border-white/10 rounded-sm p-4 space-y-4">
                <h3 className="text-white font-medium">Plan image</h3>
                {form.image_url && (
                  <img src={form.image_url} alt={form.image_alt_en || form.image_alt_el || form.name} className="w-full h-40 object-cover rounded-sm border border-white/10" />
                )}
                <div>
                  <label className="text-sm text-gray-400">Image URL</label>
                  <div className="mt-1 flex flex-col sm:flex-row gap-2">
                    <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="/api/images/..." />
                    <input ref={imageInputRef} type="file" accept="image/*" onChange={uploadPlanImage} className="hidden" />
                    <Button type="button" variant="outline" disabled={imageUploading} onClick={() => imageInputRef.current?.click()} className="border-white/20 text-white hover:bg-white/10 shrink-0">
                      <Upload size={16} className="mr-2" />
                      {imageUploading ? 'Uploading...' : 'Upload image'}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Image alt EL</label>
                    <Input value={form.image_alt_el} onChange={(e) => setForm({ ...form, image_alt_el: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Image alt EN</label>
                    <Input value={form.image_alt_en} onChange={(e) => setForm({ ...form, image_alt_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-white/20"
                />
                <span className="text-sm text-gray-400">
                  {i18n.language === 'el' ? 'Ενεργό' : 'Active'}
                </span>
              </label>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/20 text-white">
                  {i18n.language === 'el' ? 'Ακύρωση' : 'Cancel'}
                </Button>
                <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90">
                  {i18n.language === 'el' ? 'Αποθήκευση' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Plans Table */}
      <div className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Όνομα' : 'Name'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Τιμή' : 'Price'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Περίοδος' : 'Interval'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Κατάσταση' : 'Status'}</th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">{i18n.language === 'el' ? 'Ενέργειες' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-gray-500">{i18n.language === 'el' ? 'Δεν υπάρχουν πλάνα.' : 'No plans yet.'}</td></tr>
            ) : (
              plans.map((plan) => (
                <tr key={plan.id} className="border-t border-white/5">
                  <td className="p-4 text-white font-medium text-sm">{plan.name}
                    <span className="text-gray-500 text-xs ml-2">{getScopeLabel(plan.scope)}</span>
                  </td>
                  <td className="p-4 text-white text-sm">{plan.price} {plan.currency}</td>
                  <td className="p-4 text-gray-400 text-sm">{getIntervalLabel(plan.interval)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-sm text-xs ${plan.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {plan.is_active !== false ? (i18n.language === 'el' ? 'Ενεργό' : 'Active') : (i18n.language === 'el' ? 'Ανενεργό' : 'Inactive')}
                    </span>
                    {plan.stripe_price_id && <span className="ml-2 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-sm text-xs">Stripe</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10" onClick={() => openEditDialog(plan)}><Edit size={14} /></Button>
                      <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(plan.id)}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {/* Mobile */}
        <div className="md:hidden divide-y divide-white/5">
          {plans.length === 0 ? (
            <p className="p-6 text-center text-gray-500">{i18n.language === 'el' ? 'Δεν υπάρχουν πλάνα.' : 'No plans yet.'}</p>
          ) : (
            plans.map((plan) => (
              <div key={plan.id} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-white font-medium text-sm">{plan.name}</p>
                  <span className={`px-2 py-0.5 rounded-sm text-xs ${plan.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {plan.is_active !== false ? (i18n.language === 'el' ? 'Ενεργό' : 'Active') : (i18n.language === 'el' ? 'Ανενεργό' : 'Inactive')}
                  </span>
                </div>
                <p className="text-gray-400 text-xs">{plan.price} {plan.currency} / {getIntervalLabel(plan.interval)} · {getScopeLabel(plan.scope)}</p>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="border-white/20 text-white text-xs flex-1" onClick={() => openEditDialog(plan)}><Edit size={12} className="mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 text-xs" onClick={() => handleDelete(plan.id)}><Trash2 size={12} /></Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PlansManagement;
